import Big from 'big.js';
import Konva from 'konva';
import { OverlayScrollbars } from 'overlayscrollbars';
import * as buttons from 'smg/buttons';
import { AddCartItems } from 'smg/customer-api/v2/carts';
import { URLAsDataURL } from 'smg/download';
import Dropdown from 'smg/dropdown';
import { ShowError } from 'smg/errors';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { DollarFormat, RemToPx } from 'smg/util';
import * as models from 'smg/customer-api/models';
import { SetCartCount } from '../carts';
import { Relref } from '../hugo';
import * as stockitems from '../stockitem';

import bottom from '../../assets/img/custom-web-belt-buckles/editor/bottom.svg';
import left from '../../assets/img/custom-web-belt-buckles/editor/left.svg';
import right from '../../assets/img/custom-web-belt-buckles/editor/right.svg';
import top from '../../assets/img/custom-web-belt-buckles/editor/top.svg';

declare interface myWindow extends Window {
    readonly buckleAttachment: string;
    readonly buckleCartName: string;
}
declare const window: myWindow;

type customShapeConfig = Konva.ShapeConfig & {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    scaleX?: number;
    scaleY?: number;
};

void main();

// The tier table is rendered from the price catalog at build time; a quantity
// pays the highest tier at or below it.
function unitPriceAt(quantity: number): Big {
    let price = new Big(0);
    for (const $tier of Array.from(document.querySelectorAll<HTMLElement>('.stock-item-tier-price'))) {
        if ($tier.dataset.price && parseInt($tier.dataset.qty ?? '', 10) <= quantity) {
            price = new Big($tier.dataset.price);
        }
    }
    return price;
}

async function main() {
    OverlayScrollbars(document.querySelector('#gallery-image-container') as HTMLElement, {});

    const imgGalleryContainers = [document.querySelector('#scrollbar-container') as HTMLElement, document.querySelector('#inner-xl-gallery-container') as HTMLElement];

    const $addToCartForm = document.querySelector('#add-to-cart-form') as HTMLFormElement;
    const $addToCartFieldset = $addToCartForm.querySelector('fieldset') as HTMLFieldSetElement;

    const $customizeTemplate = template.Clone('#customize-template').firstElementChild as HTMLElement;
    const $firstChild = $customizeTemplate;

    const $uploadRequired = $firstChild.querySelectorAll('.upload-required');

    const $canvasUploaderLabel = $firstChild.querySelector('#canvas-uploader') as HTMLLabelElement;
    const $uploadInput = $canvasUploaderLabel.querySelector('input') as HTMLInputElement;

    const $dropContainer = $firstChild.querySelector('#drop-container') as HTMLElement;
    $dropContainer.classList.remove('d-none');
    const $stageUploadButton = $firstChild.querySelector('#stage-upload-button') as HTMLButtonElement;
    $stageUploadButton.addEventListener('click', () => $canvasUploaderLabel.click());

    let img: HTMLImageElement;
    let outlineImg: HTMLImageElement;

    const $metalTypeContainer = $firstChild.querySelector('#metal-type-container') as HTMLElement;
    const $metalTypeChildren = Array.from($metalTypeContainer.children) as HTMLLabelElement[];

    const $customizeBtn = document.querySelector('#customize') as HTMLButtonElement;
    $customizeBtn.addEventListener('click', e => {
        e.preventDefault();
        ($metalTypeChildren[0].querySelector('input') as HTMLInputElement).checked = true;

        createCustomizeModal();

        for (const $child of $metalTypeChildren) {
            $child.addEventListener('click', () => {
                for (const $child of $metalTypeChildren) {
                    $child.classList.remove('metal-active');
                }

                $child.classList.add('metal-active');

                const metalTypeID = ((document.querySelector('input[name=metal-type]:checked') as HTMLElement).
                    closest('label') as HTMLElement).id;
                img.src = `${Relref('/')}img/custom-belt-buckles/editor/${metalTypeID}-fs8.png`;
                bgImage.image(img);
            });
        }
        $metalTypeChildren[0].dispatchEvent(new Event('click'));
    });

    // Konva

    const $canvasParent = $firstChild.querySelector('#konva-target') as HTMLDivElement;
    const $stage = $canvasParent.parentElement as HTMLElement;

    const stage = new Konva.Stage({
        container: $canvasParent,
    });

    const bgImage = await new Promise<Konva.Image>(resolve => {
        img = new Image();
        img.onload = () =>
            resolve(new Konva.Image({
                image: img,
                draggable: false,
                listening: false,
            }));
        img.src = `${Relref('/')}img/custom-belt-buckles/editor/${($metalTypeChildren[0].closest('label') as HTMLElement).id}-fs8.png`;
    });

    const innerOutlineImage = await new Promise<Konva.Image>(resolve => {
        outlineImg = new Image();
        outlineImg.onload = () =>
            resolve(new Konva.Image({
                image: outlineImg,
                draggable: false,
                listening: false,
            }));
        outlineImg.src = `${Relref('/')}img/custom-belt-buckles/editor/BeltBuckleGraphic2.png`;
    });

    const bgLayer = new Konva.Layer();
    stage.add(bgLayer);
    bgLayer.add(bgImage);

    const newLayer2 = new Konva.Layer();
    stage.add(newLayer2);
    newLayer2.add(innerOutlineImage);

    const padding = {
        top: RemToPx(0),
        right: RemToPx(0),
        bottom: RemToPx(0),
        left: RemToPx(0),
    };

    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;
    function fitStageIntoParentContainer(firstRun = true) {
        const $stageChildren: ChildNode[] = [];
        if (!firstRun) {
            $stageChildren.push(...Array.from($stage.childNodes));
            $stage.innerHTML = '';
        }

        stage.width($stage.offsetWidth);
        stage.height(bgImage.height() / bgImage.width() * $stage.offsetWidth);

        if ($stageChildren) {
            for (const c of $stageChildren) {
                $stage.appendChild(c);
            }
        }

        const originalRatios = {
            width: ($stage.offsetWidth - padding.left - padding.right) / bgImage.width(),
            height: ($stage.offsetHeight - padding.top - padding.bottom) / bgImage.height(),
        };

        // formula for cover:
        const coverRatio = Math.min(originalRatios.width, originalRatios.height);

        // result:
        scale = coverRatio;

        stage.scale({ x: scale, y: scale });
        offsetX = ((((stage.width() - (padding.left + padding.right)) - (bgImage.width() * scale)) / 2) + padding.left) / scale;
        stage.offsetX(-offsetX);
        offsetY = ((((stage.height() - (padding.top + padding.bottom)) - (bgImage.height() * scale)) / 2) + padding.top) / scale;
        stage.offsetY(-offsetY);

        $stage.setAttribute('style', `--scale:${scale};--offsetY:${offsetY}px;--offsetX:${offsetX}px;--height:${stage.height()}px`);
    }

    fitStageIntoParentContainer();
    window.addEventListener('resize', () => fitStageIntoParentContainer(false));

    const $topText = $firstChild.querySelector('#top-text-input') as HTMLInputElement;
    const $bottomText = $firstChild.querySelector('#bottom-text-input') as HTMLInputElement;
    const $rightText = $firstChild.querySelector('#right-text-input') as HTMLInputElement;
    const $leftText = $firstChild.querySelector('#left-text-input') as HTMLInputElement;
    const $fontStyle = $firstChild.querySelector('#font-select') as HTMLSelectElement;

    const $sortFontOptions = () => {
        // Make a new map tying the name of the font to it's htmlelement
        const $fontOptions = new Map<string, HTMLOptionElement>();
        for (const $ of Array.from($fontStyle.options)) {
            $fontOptions.set($.textContent ?? '', $);
        }

        // Grab all font names
        const $fontNames = [];
        for (const $ of Array.from($fontStyle.options)) {
            $fontNames.push($.textContent);
        }

        // Alphabetically sort the names of the fonts
        const $sortedFontNames = $fontNames.sort();

        // With the sorted fonts, the options get sorted by 'getting' the values from the font names
        const $sortedFontOptions = [];
        for (const $ of Array.from($sortedFontNames)) {
            $sortedFontOptions.push($fontOptions.get($ ?? ''));
        }

        $fontStyle.innerHTML = '';
        for (const $ of $sortedFontOptions) {
            $fontStyle.append($ ?? '');
            // If the element exists, deselect it.
            if ($) {
                $.selected = false;
            }
        }
    };

    $sortFontOptions();

    new Dropdown($fontStyle);

    interface newTextFieldOpts {
        $input: HTMLInputElement;
        svg: string;
        textPathSelector: string;
    }
    const newTextField = (opts: newTextFieldOpts) => {
        const updateSVGFont = async () => {
            const fontURL = `//${window.location.host}${Relref('/')}fonts/${$fontStyle.selectedOptions[0].getAttribute('data-file') ?? ''}`;

            $style.innerHTML = `@font-face {
                font-family:&quot;my-custom-font&quot;;
                src:url(&quot;${html.Escape(await URLAsDataURL(fontURL))}&quot;);
                font-display:auto;font-style:normal;font-weight:400;font-stretch:normal;
            }`;
        };

        $fontStyle.addEventListener('change', async () => {
            await updateSVGFont();
            drawText();
        });

        const $svg = html.FromString(opts.svg).firstElementChild as SVGSVGElement;
        const $style = $svg.querySelector('#style2') as HTMLStyleElement;
        const $text = $svg.querySelector(opts.textPathSelector) as SVGTextPathElement;

        void updateSVGFont().then(() => drawText());

        const getSVGImage = () => {
            const img = new Image();
            img.src = URL.createObjectURL(new Blob([$svg.outerHTML], { type: 'image/svg+xml' }));
            img.height = 100;
            return img;
        };
        const textImage = new Konva.Image({
            image: getSVGImage(),
            width: 2000,
            height: 1448,
            x: 0,
            y: 0,
        });
        const textLayer = new Konva.Layer();
        textLayer.add(textImage);
        stage.add(textLayer);

        const drawText = () => {
            textImage.image(getSVGImage());
        };
        opts.$input.addEventListener('input', () => {
            $text.textContent = opts.$input.value;
            drawText();
        });
    };

    newTextField({
        $input: $topText,
        svg: top,
        textPathSelector: '#textPath7379',
    });

    newTextField({
        $input: $leftText,
        svg: left,
        textPathSelector: '#textPath7402',
    });

    newTextField({
        $input: $rightText,
        svg: right,
        textPathSelector: '#textPath50543',
    });

    newTextField({
        $input: $bottomText,
        svg: bottom,
        textPathSelector: '#textPath34276',
    });

    const $stageLoader = $firstChild.querySelector('#stage-loader') as HTMLElement;

    const showLoading = () => {
        $stage.style.opacity = '0.5';
        $stage.style.pointerEvents = 'none';
        $stageLoader.classList.remove('d-none');
    };

    const hideLoading = () => {
        $stage.style.opacity = '';
        $stage.style.pointerEvents = '';
        $stageLoader.classList.add('d-none');
    };

    // start uploader

    let uploadID = 0;
    const uploadLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        width: bgImage.width(),
        height: bgImage.height(),
    });
    const uploadImage = new Konva.Image({
        image: new Image(),
    });
    uploadLayer.add(uploadImage);
    stage.add(uploadLayer);

    uploadLayer.add(uploadImage);

    const uploadTransformerLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        visible: false,
    });
    const uploaderTransformer = new Konva.Transformer({
        anchorStroke: '#fff',
        borderStroke: '#1a73e8',
        anchorSize: 9,
        anchorFill: '#1a73e8',
        centeredScaling: true,
    });
    uploadTransformerLayer.add(uploaderTransformer);
    stage.add(uploadTransformerLayer);

    const transformerShape = new Konva.Rect({
        draggable: true,
    });
    uploadTransformerLayer.add(transformerShape);
    uploaderTransformer.nodes([transformerShape]);

    const $editBtnSm = $firstChild.querySelector('#edit-btn-sm') as HTMLButtonElement;
    const $editBtnLg = $firstChild.querySelector('#edit-btn-lg') as HTMLButtonElement;
    const $doneBtn = $firstChild.querySelector('#done-btn') as HTMLButtonElement;
    const $resetBtn = $firstChild.querySelector('#reset-btn') as HTMLButtonElement;
    const $changeImage = $firstChild.querySelector('#change-image') as HTMLButtonElement;
    const $inputCol = $firstChild.querySelector('#inputs-col') as HTMLElement;
    const $inputsFieldset = $firstChild.querySelector('#inputs-fieldset') as HTMLElement;
    const $modalError = $firstChild.querySelector('.modal-error') as HTMLElement;

    let inTransformMode: boolean = false;

    const startTransformMode = () => {
        if (window.screen.width < 1200) {
            $inputsFieldset?.setAttribute('disabled', 'true');
        }
        $editBtnSm.classList.add('d-none');
        $editBtnLg.classList.add('d-none');
        $changeImage.classList.remove('d-none');
        $doneBtn.classList.remove('d-none');
        $resetBtn.classList.remove('d-none');

        inTransformMode = true;
        uploadTransformerLayer.show();
    };

    const stopTransformMode = () => {
        if (!uploadID) {
            return;
        }
        $inputsFieldset?.removeAttribute('disabled');
        $editBtnLg.classList.remove('d-none');
        $editBtnSm.classList.remove('d-none');
        $changeImage.classList.add('d-none');
        $doneBtn.classList.add('d-none');
        $resetBtn.classList.add('d-none');

        inTransformMode = false;
        uploadTransformerLayer.hide();
    };

    window.addEventListener('resize', () => {
        if (window.screen.width < 1200) {
            if (inTransformMode) {
                $inputsFieldset?.setAttribute('disabled', 'true');
            }
        } else {
            $inputsFieldset?.removeAttribute('disabled');
        }
    });

    const maskActualWidth = 1769;
    const maskActualHeight = 1137;
    const maskOffsetX = 115;
    const maskOffsetY = 154;

    let setImageSizeAndPosition: () => void;

    new uploads.Uploader($canvasUploaderLabel, {
        dropZones: [$dropContainer],
        start: showLoading,
        stop: hideLoading,
        success: (thumb, newUploadID, blobURL) => {
            const $modalErr = document.querySelector<HTMLElement>('.modal-error');
            if ($modalErr?.textContent === 'Please upload an image.') {
                $modalErr.classList.add('d-none');
            }
            uploadID = newUploadID;

            const mask = new Image();
            mask.onload = () => {
                const img = new Image();
                img.onload = () => {
                    const originalRatios = {
                        width: maskActualWidth / img.naturalWidth,
                        height: maskActualHeight / img.naturalHeight,
                    };

                    // formula for cover:
                    const coverRatio = Math.max(originalRatios.width, originalRatios.height);

                    // result:
                    const newImageWidth = img.naturalWidth * coverRatio;
                    const newImageHeight = img.naturalHeight * coverRatio;

                    const newImageX = maskOffsetX - (newImageWidth - maskActualWidth) / 2;
                    const newImageY = maskOffsetY - (newImageHeight - maskActualHeight) / 2;

                    const image = new Konva.Shape<customShapeConfig>({
                        sceneFunc: ctx => {
                            ctx.save();
                            ctx.drawImage(mask, -image.x(), -image.y(), mask.naturalWidth, mask.naturalHeight);
                            // TODO typescript version bug?
                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-in';

                            const toRadians = Math.PI / 180;
                            const angleInDeg = uploaderTransformer.rotation();
                            ctx.rotate(angleInDeg * toRadians);
                            ctx.drawImage(img, 0, 0, uploaderTransformer.width() / stage.scaleX(), uploaderTransformer.height() / stage.scaleX());
                            ctx.rotate(-angleInDeg * toRadians);

                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-over';
                            ctx.restore();
                        },
                        hitFunc: ctx => {
                            ctx.rect(0, 0, uploaderTransformer.width() / stage.scaleX(), uploaderTransformer.height() / stage.scaleX());
                            ctx.fillStrokeShape(image);
                        },
                    });

                    setImageSizeAndPosition = () => {
                        transformerShape.rotation(0);
                        image.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        transformerShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });
                        transformerShape.draw();
                    };
                    setImageSizeAndPosition();

                    uploadLayer.add(image);

                    transformerShape.off('dragmove');
                    transformerShape.on('dragmove', () => image.position(transformerShape.position()));

                    transformerShape.off('transform');
                    transformerShape.on('transform', () => image.position(transformerShape.position()));

                    image.on('click', () => startTransformMode);

                    hideLoading();
                };
                img.src = blobURL;

                newLayer2.hide();
                $dropContainer.parentElement?.removeChild($dropContainer);
                startTransformMode();

                $uploadRequired.forEach($el => $el.classList.add('d-none'));
            };
            mask.src = `${Relref('/')}img/custom-belt-buckles/editor/mask-fs8.png`;
        },
    });

    [$editBtnSm, $editBtnLg].forEach(el => el.addEventListener('click', e => {
        e.preventDefault();
        startTransformMode();
    }));
    $changeImage.addEventListener('click', e => {
        e.preventDefault();
        $canvasUploaderLabel.click();
    });
    $resetBtn.addEventListener('click', e => {
        e.preventDefault();
        setImageSizeAndPosition?.();
    });
    $doneBtn.addEventListener('click', e => {
        e.preventDefault();
        stopTransformMode();
    });

    // stop uploader

    const $addToCart = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const $increaseQty = document.querySelector('#increase-qty') as HTMLButtonElement;
    const $decreaseQty = document.querySelector('#decrease-qty') as HTMLButtonElement;
    const $quantity = document.querySelector('#quantity-input') as HTMLInputElement;
    const $editDesign = document.querySelector('#edit-current-design') as HTMLElement;

    [$increaseQty, $decreaseQty, $quantity].forEach(el => {
        if (el instanceof HTMLInputElement) {
            el.addEventListener('keypress', e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    $quantity.blur();
                }
            });
        } else {
            el.addEventListener('click', e => {
                e.preventDefault();
                let valNum = parseInt($quantity.value, 10);
                if (e.currentTarget === $increaseQty) {
                    valNum++;
                } else {
                    valNum--;
                }

                $quantity.value = valNum.toString();
            });
        }
    });

    $editDesign.addEventListener('click', e => {
        e.preventDefault();

        createCustomizeModal();
    });

    let addToCart: () => void;
    $addToCart.addEventListener('click', () => addToCart());

    function createCustomizeModal() {
        const m = new Modal({
            body: $firstChild,
            size: 'modal-xl',
            cancelButtonClass: 'btn btn-lg btn-outline-secondary',
            confirmButtonClass: 'btn btn-lg btn-primary',
            confirmText: 'Complete Design',
            cancelText: 'Cancel',
            outsideClosable: false,
            reverseButtonOrder: true,
            modalDialogScrollable: true,
            submitAutoFocus: true,
            onClose: () => {
                $modalError.classList.add('d-none');
            },
            preSubmit: () => {
                if (!uploadID) {
                    $uploadInput.setCustomValidity('Please upload a file.');

                    $modalError.classList.remove('d-none');

                    return false;
                } else {
                    $uploadInput.setCustomValidity('');
                    $modalError.classList.add('d-none');
                }
            },
            onSubmit: () => {
                const $plating = (document.querySelector('input[name=metal-type]:checked') as HTMLInputElement).value;

                stopTransformMode();

                const url = stage.toDataURL({
                    quality: 1,
                    mimeType: 'image/png',
                    x: offsetX * scale,
                    y: offsetY * scale,
                    width: 2000 * scale,
                    height: 1456 * scale,
                });

                bgLayer.hide();
                const frontURL = stage.toDataURL({
                    quality: 1,
                    mimeType: 'image/png',
                    x: offsetX * scale,
                    y: offsetY * scale,
                    width: 2000 * scale,
                    height: 1448 * scale,
                    pixelRatio: (1200 * 3.5) / (2000 * scale),
                });
                bgLayer.show();

                for (const container of imgGalleryContainers) {
                    const $aTemplate = template.Clone('#custom-product-image').firstElementChild as HTMLAnchorElement;
                    const $a = $aTemplate;
                    const $img = $a?.firstElementChild as HTMLImageElement;

                    $a.href = url;
                    $img.src = url;

                    if (container.firstElementChild?.classList.contains('custom-design')) {
                        container.firstElementChild.remove();
                    }
                    container.insertBefore($a, container.firstElementChild);
                }

                $customizeBtn.classList.add('d-none');
                $addToCartForm.classList.remove('d-none');

                addToCart = () => {
                    buttons.Start($addToCart);
                    buttons.Start($editDesign);
                    $addToCartFieldset.disabled = true;

                    const colorLayerPromise = stockitems.ColorLayer(frontURL);
                    const whiteLayerPromise = stockitems.WhiteLayer(frontURL);

                    const addToCartPromise = (async () => uploads.NewFromURL([
                        {
                            name: 'personalized-belt-buckle.png',
                            mime: 'image/png',
                            url,
                        },
                        {
                            name: '.front.jpg',
                            mime: 'image/jpeg',
                            url: await colorLayerPromise,
                        },
                        {
                            name: '.front-white.jpg',
                            mime: 'image/jpeg',
                            url: await whiteLayerPromise,
                        },
                    ]).then(resp => AddCartItems({
                        uploadID: resp[0].uploadID,
                        quantity: parseInt($quantity.value, 10),
                        options: [
                            {
                                name: 'Top Text',
                                value: $topText.value,
                            },
                            {
                                name: 'Bottom Text',
                                value: $bottomText.value,
                            },
                            {
                                name: 'Right Text',
                                value: $rightText.value,
                            },
                            {
                                name: 'Left Text',
                                value: $leftText.value,
                            },
                            {
                                name: 'Font Style',
                                value: $fontStyle.value,
                            },
                        ],
                        cartItemUploadIDs: [uploadID, ...resp.slice(1).map(u => u.uploadID)],
                        uploadLineItem: {
                            name: window.buckleCartName,
                            lineItem: 'belt buckles',
                            type: 'Made in USA - Simulated Enamel',
                            size: '3.5" × 2.5"',
                            networkSKUID: models.NetworkSKUID.NETWORK_SK_US_PERSONALIZED_BELT_BUCKLES,
                            childLineItems: [
                                {
                                    child: 'Attachments',
                                    option: window.buckleAttachment,
                                },
                                {
                                    child: 'plating',
                                    option: $plating,
                                },
                            ],
                        },
                    }).then(resp => {
                        SetCartCount(resp.cartItemsCount);

                        const $productInfo = html.FromString(`
                                <div class="w-80 mx-auto d-flex flex-column flex-sm-row">
                                    <div id="attached-image" class="w-100 w-sm-60 d-flex align-items-center justify-content-center justify-content-sm-start mb-8 mb-sm-0">
                                        <a href="${url}" data-fancybox style="width: 80%; height: auto;" class="d-flex justify-content-center">
                                            <img id="just-added-img" src="${url}" style="max-width: 100%; max-height: 100%;"/>
                                        </a>
                                    </div>

                                    <div class="w-100 w-sm-40 d-flex flex-column gap-2 gap-sm-4 text-center text-sm-start">
                                        <span style="font-size: 1.25rem; font-weight: 600;">Customizable Web Belt Buckle</span>

                                        <div>Quantity: <span style="font-weight: 700;" id="quantity">${$quantity.value}</span></div>
                                        <div>Cost: <span style="font-weight: 700;">${DollarFormat(unitPriceAt(parseInt($quantity.value, 10)).times(parseInt($quantity.value, 10)))}</span></div>
                                    </div>
                                </div>`).firstElementChild as HTMLElement;

                        const $justAddedBody = template.Clone('#just-added').firstElementChild as HTMLElement;
                        const $justAddedProductInfo = $justAddedBody.querySelector('#product-info') as HTMLElement;

                        $justAddedProductInfo.append($productInfo);

                        const justAddedModal = new Modal({
                            body: $justAddedBody,
                            size: 'modal-sm',
                            cancelButtonClass: 'd-none',
                            confirmButtonClass: 'd-none',
                            outsideClosable: false,
                            onClose: () => {
                                buttons.Stop($addToCart);
                                buttons.Stop($editDesign);
                                $addToCartFieldset.disabled = false;
                            },
                        });

                        const modalBody = justAddedModal.$element.querySelector('.modal-body') as HTMLElement;
                        modalBody.classList.add('p-0');

                        const modalFooter = justAddedModal.$element.querySelector('.modal-footer') as HTMLElement;
                        modalFooter.classList.add('d-none');

                        const $toCartBtn = $justAddedBody.querySelector('#to-cart') as HTMLButtonElement;
                        $toCartBtn.addEventListener('click', e => {
                            e.preventDefault();

                            window.location.href = Relref('/cart');
                        });
                        buttons.Start($toCartBtn);
                        void addToCartPromise.then(() => buttons.Stop($toCartBtn));

                        const $newDesign = $justAddedBody.querySelector('#new-design') as HTMLButtonElement;
                        $newDesign.addEventListener('click', e => {
                            e.preventDefault();
                            window.location.reload();
                        });

                        const $previousDesign = $justAddedBody.querySelector('#previous-design') as HTMLButtonElement;
                        $previousDesign.addEventListener('click', e => {
                            e.preventDefault();
                            justAddedModal.close();
                            $addToCartFieldset.disabled = false;
                        });
                    }).catch(ShowError).
                        finally(() => {
                            $addToCartFieldset.disabled = false;
                            buttons.Stop($addToCart);
                            buttons.Stop($editDesign);
                        })))();
                };
            },
        });

        const $modalContent = m.$element.querySelector('.modal-content') as HTMLElement;
        const $modalBody = m.$element.querySelector('.modal-body') as HTMLElement;
        $modalBody.style.minHeight = '800px';
        const $modalCloseBtn = html.FromString(`
        <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close" style="z-index: 2;"></button>
        `).firstElementChild as HTMLButtonElement;

        $modalContent.insertBefore($modalCloseBtn, $modalContent.firstElementChild);
        $inputCol.setAttribute('style', `--inputsColHeight:${$modalContent.offsetHeight}px;`);
        window.addEventListener('resize', () => {
            $inputCol.setAttribute('style', `--inputsColHeight:${$modalContent.offsetHeight}px;`);
        });
        OverlayScrollbars($inputCol, {});

        fitStageIntoParentContainer();
    }
}